import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { usePage } from '@inertiajs/react';
import React from 'react';

interface BonusModule {
    emoji: string;
    name: string;
    headline: string;
    description: string;
    url: string | null;
}

interface BonusModulesConfig {
    coming_soon_title: string;
    coming_soon_subtitle: string;
    section_title: string;
    modules: BonusModule[];
}

interface PageProps {
    static_page: {
        id: number;
        name: string;
        slug: string;
        config: BonusModulesConfig;
    };
}

const BonusModuleCard: React.FC<{ module: BonusModule }> = ({ module }) => (
    <GlassCard className="flex flex-col overflow-hidden">
        <div className="flex justify-center pb-4 pt-8">
            <span className="text-6xl">{module.emoji}</span>
        </div>
        <div className="bg-teal-500 px-4 py-3 text-center">
            <h3 className="text-xl font-bold italic text-white">{module.name}</h3>
        </div>
        <div className="flex flex-1 flex-col gap-3 p-6">
            <p className="font-semibold text-gray-900 dark:text-white">
                {module.emoji} {module.headline}
            </p>
            <p className="flex-1 text-sm text-gray-600 dark:text-neutral-300">
                {module.description}
            </p>
        </div>
        <div className="px-6 pb-6">
            {module.url ? (
                <a
                    href={module.url}
                    className="block w-full rounded-full bg-teal-500 py-2 text-center font-semibold text-white transition hover:bg-teal-600"
                >
                    Acceder
                </a>
            ) : (
                <div className="flex items-center justify-center gap-2 rounded-full border-2 border-amber-300 bg-amber-50 py-2 text-sm font-semibold text-amber-700 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                    🟡 Próximamente
                </div>
            )}
        </div>
    </GlassCard>
);

const BonusModulesContent: React.FC = () => {
    const { static_page } = usePage<{ static_page: PageProps['static_page'] }>().props;
    const config = static_page.config;

    return (
        <AppLayout>
            <PageTitle title={static_page.name} />
            <div className="mx-auto max-w-5xl space-y-10">
                <div className="space-y-2 text-center">
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                        {config.coming_soon_title}
                    </h2>
                    <p className="text-gray-600 dark:text-neutral-400">
                        {config.coming_soon_subtitle}
                    </p>
                </div>

                <hr className="border-dashed border-gray-300 dark:border-neutral-600" />

                <div className="space-y-6">
                    <h2 className="text-center text-2xl font-bold text-gray-900 dark:text-white">
                        {config.section_title}
                    </h2>
                    <div className="grid gap-6 md:grid-cols-3">
                        {config.modules.map((module, index) => (
                            <BonusModuleCard key={index} module={module} />
                        ))}
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

const BonusModules: React.FC = () => (
    <ThemeWrapper>
        <BonusModulesContent />
    </ThemeWrapper>
);

export default BonusModules;
